import React, { useState } from "react";
import { gql, useLazyQuery } from "@apollo/client";
import { Link } from "react-router-dom";

const GET_PAYMENT_COLLECTION_REPORT = gql`
  query GetPaymentCollectionReport($data: PaymentCollectionReportInput!) {
    getPaymentCollectionReport(data: $data) {
      status
      message
      rows {
        period
        bookings
        razorpayAmount
        razorpayCount
        onlineOtherAmount
        onlineOtherCount
        cashAmount
        cashCount
        totalAmount
      }
      total {
        bookings
        razorpayAmount
        razorpayCount
        onlineOtherAmount
        onlineOtherCount
        cashAmount
        cashCount
        unknownAmount
        totalAmount
        walletAmount
        unverifiedCount
        unverifiedAmount
      }
      razorpay {
        collectedAmount
        collectedCount
        refundedAmount
        refundedCount
        failedAmount
        failedCount
        customerBookingAmount
        categories {
          kind
          label
          amount
          count
        }
        payments {
          kind
          kindLabel
          paymentId
          paidAt
          amount
          status
          description
          phone
          bookingId
          dbPaymentType
          unlinkedId
          unlinkedStatus
        }
      }
    }
  }
`;

const inr = (n) =>
  `₹${Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 0 })}`;
const pct = (a, t) => (t ? `${((100 * (a || 0)) / t).toFixed(1)}%` : "–");
const istToday = () =>
  new Date(Date.now() + 330 * 60000).toISOString().slice(0, 10);
const monthsAgoStart = (n) => {
  const d = new Date(Date.now() + 330 * 60000);
  return new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth() - n, 1))
    .toISOString()
    .slice(0, 10);
};
const periodLabel = (p, groupBy) => {
  if (!p) return "Total";
  const d = new Date(`${p}T00:00:00`);
  return groupBy === "month"
    ? d.toLocaleDateString("en-IN", { month: "short", year: "numeric" })
    : d.toLocaleDateString("en-IN", { day: "2-digit", month: "short" });
};

const KIND_STYLES = {
  BOOKING_PAYMENT_NOT_RECORDED: "bg-amber-100 text-amber-800",
  EMPLOYEE_CASH_DEPOSIT: "bg-purple-100 text-purple-700",
  OTHER_INCOME: "bg-blue-100 text-blue-700",
  UNIDENTIFIED: "bg-red-100 text-red-700",
  APP_SESSION_MISSING: "bg-gray-100 text-gray-700",
  TEST: "bg-gray-100 text-gray-600",
  APP_BOOKING_PAYMENT: "bg-green-100 text-green-700",
};

const Cell = ({ amount, count, total }) => (
  <td className="px-3 py-2 text-right whitespace-nowrap">
    <div className="font-medium">{inr(amount)}</div>
    <div className="text-xs text-gray-500">
      {count != null && `${count} · `}
      {pct(amount, total)}
    </div>
  </td>
);

/**
 * Collection report (same logic as the payment-collection-report skill):
 * completed bookings by service date split into Razorpay / online (not
 * Razorpay) / cash, optionally compared with actual Razorpay payments.
 */
const PaymentCollectionReport = ({ onClose }) => {
  const [startDate, setStartDate] = useState(monthsAgoStart(2));
  const [endDate, setEndDate] = useState(istToday());
  const [groupBy, setGroupBy] = useState("month");
  const [includeRazorpay, setIncludeRazorpay] = useState(false);
  const [ranGroupBy, setRanGroupBy] = useState("month");

  const [run, { data, loading, error }] = useLazyQuery(
    GET_PAYMENT_COLLECTION_REPORT,
    { fetchPolicy: "network-only" },
  );
  const report = data?.getPaymentCollectionReport;
  const t = report?.total;
  const rz = report?.razorpay;

  const handleRun = () => {
    setRanGroupBy(groupBy);
    run({ variables: { data: { startDate, endDate, groupBy, includeRazorpay } } });
  };

  const rows = report ? [...(report.rows || []), ...(t ? [{ ...t, period: null }] : [])] : [];

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold">Collection report</h2>
          <p className="text-xs text-gray-500">
            Completed bookings by service date (IST): how much came through
            Razorpay, online outside Razorpay, and cash.
          </p>
        </div>
        <button type="button" onClick={onClose} className="text-gray-500">
          ✕
        </button>
      </div>

      <div className="flex flex-wrap items-end gap-3 bg-gray-50 border rounded-md p-3">
        <label className="text-sm">
          From
          <input
            type="date"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
            className="block mt-1 border rounded-md px-2 py-1"
          />
        </label>
        <label className="text-sm">
          To
          <input
            type="date"
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
            className="block mt-1 border rounded-md px-2 py-1"
          />
        </label>
        <label className="text-sm">
          Group by
          <select
            value={groupBy}
            onChange={(e) => setGroupBy(e.target.value)}
            className="block mt-1 border rounded-md px-2 py-1"
          >
            <option value="month">Month</option>
            <option value="day">Day</option>
            <option value="none">Total only</option>
          </select>
        </label>
        <label className="text-sm flex items-center gap-2 pb-1">
          <input
            type="checkbox"
            checked={includeRazorpay}
            onChange={(e) => setIncludeRazorpay(e.target.checked)}
          />
          Compare with Razorpay (slower)
        </label>
        <button
          type="button"
          onClick={handleRun}
          disabled={loading}
          className="ml-auto px-5 py-2 bg-[#FBB033] rounded-md font-semibold disabled:opacity-60"
        >
          {loading ? "Running…" : "Run report"}
        </button>
      </div>

      {error && <p className="text-sm text-red-600">{error.message}</p>}
      {report && report.status !== "SUCCESS" && (
        <p className="text-sm text-red-600">{report.message}</p>
      )}

      {t && (
        <>
          <div className="overflow-x-auto border rounded-md">
            <table className="min-w-full text-sm">
              <thead className="bg-gray-50 text-gray-600">
                <tr>
                  <th className="px-3 py-2 text-left">Period</th>
                  <th className="px-3 py-2 text-right">Bookings</th>
                  <th className="px-3 py-2 text-right">Razorpay</th>
                  <th className="px-3 py-2 text-right">Online (not Razorpay)</th>
                  <th className="px-3 py-2 text-right">Cash</th>
                  <th className="px-3 py-2 text-right">Total</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr
                    key={r.period || "total"}
                    className={`border-t ${r.period ? "" : "bg-gray-50 font-semibold"}`}
                  >
                    <td className="px-3 py-2">{periodLabel(r.period, ranGroupBy)}</td>
                    <td className="px-3 py-2 text-right">{r.bookings}</td>
                    <Cell amount={r.razorpayAmount} count={r.razorpayCount} total={r.totalAmount} />
                    <Cell amount={r.onlineOtherAmount} count={r.onlineOtherCount} total={r.totalAmount} />
                    <Cell amount={r.cashAmount} count={r.cashCount} total={r.totalAmount} />
                    <td className="px-3 py-2 text-right font-medium">{inr(r.totalAmount)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <ul className="text-xs text-gray-600 space-y-1 list-disc pl-5">
            <li>
              Count under each amount = bookings with money in that column.
              Wallet credit used ({inr(t.walletAmount)}) isn't collected money
              and is excluded.
            </li>
            {t.unverifiedCount > 0 && (
              <li className="text-amber-700">
                {t.unverifiedCount} bookings not yet admin-verified carry{" "}
                {inr(t.unverifiedAmount)} of non-Razorpay money - their
                online/cash split may change on verification.
              </li>
            )}
            {t.unknownAmount > 0 && (
              <li>Uncategorised (payment type missing): {inr(t.unknownAmount)}</li>
            )}
          </ul>
        </>
      )}

      {rz && (
        <div className="space-y-3 border-t pt-4">
          <div>
            <h3 className="font-semibold">Razorpay (by payment date)</h3>
            <p className="text-xs text-gray-500">
              Actual Razorpay payments for these dates - the total should match
              the Razorpay dashboard's Collected Amount.
            </p>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            {[
              ["Collected (dashboard)", rz.collectedAmount, `${rz.collectedCount} payments`],
              ["Refunded", rz.refundedAmount, `${rz.refundedCount} payments`],
              ["Customer booking money", rz.customerBookingAmount, "after refunds, deposits, other"],
              ["Failed attempts", rz.failedAmount, `${rz.failedCount} · not money`],
            ].map(([label, amount, sub]) => (
              <div key={label} className="border rounded-md p-3">
                <div className="text-xs text-gray-500">{label}</div>
                <div className="text-lg font-semibold">{inr(amount)}</div>
                <div className="text-xs text-gray-500">{sub}</div>
              </div>
            ))}
          </div>
          <div className="flex flex-wrap gap-2">
            {rz.categories.map((c) => (
              <span
                key={c.kind}
                className={`px-2 py-1 rounded-md text-xs ${KIND_STYLES[c.kind] || "bg-gray-100"}`}
              >
                {c.label}: {inr(c.amount)} ({c.count})
              </span>
            ))}
          </div>

          {rz.payments.length > 0 && (
            <>
              <p className="text-xs text-gray-600">
                Payments that aren't plain app booking payments. A booking
                payment "not recorded" is counted as Online (not Razorpay) or
                Cash in the table above but really came through Razorpay;
                professional cash deposits are cash already counted above - not
                extra revenue.
              </p>
              <div className="overflow-x-auto border rounded-md">
                <table className="min-w-full text-sm">
                  <thead className="bg-gray-50 text-gray-600 text-left">
                    <tr>
                      <th className="px-3 py-2">Type</th>
                      <th className="px-3 py-2">Paid on</th>
                      <th className="px-3 py-2 text-right">Amount</th>
                      <th className="px-3 py-2">Description</th>
                      <th className="px-3 py-2">Phone</th>
                      <th className="px-3 py-2">Booking</th>
                      <th className="px-3 py-2" />
                    </tr>
                  </thead>
                  <tbody>
                    {rz.payments.map((p) => (
                      <tr key={p.paymentId} className="border-t align-top">
                        <td className="px-3 py-2">
                          <span
                            className={`px-2 py-0.5 rounded-full text-xs whitespace-nowrap ${
                              KIND_STYLES[p.kind] || "bg-gray-100"
                            }`}
                          >
                            {p.kindLabel}
                          </span>
                          {p.status === "refunded" && (
                            <div className="text-xs text-gray-500 mt-1">refunded</div>
                          )}
                        </td>
                        <td className="px-3 py-2 whitespace-nowrap">
                          {new Date(p.paidAt).toLocaleDateString("en-IN", {
                            day: "2-digit",
                            month: "short",
                            timeZone: "Asia/Kolkata",
                          })}
                        </td>
                        <td className="px-3 py-2 text-right">{inr(p.amount)}</td>
                        <td className="px-3 py-2">
                          <div>{p.description || "—"}</div>
                          <div className="text-xs text-gray-400 font-mono">{p.paymentId}</div>
                        </td>
                        <td className="px-3 py-2">{p.phone || "—"}</td>
                        <td className="px-3 py-2">
                          {p.bookingId || "—"}
                          {p.dbPaymentType && (
                            <div className="text-xs text-gray-500">DB: {p.dbPaymentType}</div>
                          )}
                        </td>
                        <td className="px-3 py-2 whitespace-nowrap">
                          {p.unlinkedStatus === "UNLINKED" && (
                            <Link to="/admin/razorpay-payments" className="text-blue-600 text-xs">
                              Link →
                            </Link>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default PaymentCollectionReport;
